import Helpers from '~/common/Helpers.js';
import { _ } from '~/common/Utils.js';

const filter = ({ value, onValue, context, filters = [] }) => {
    const subscriptions = [];
    const result = {
        dispose: () => {
            subscriptions.map(subscription => subscription.dispose());
        }
    };

    const computedFilters = _.Bind.ComputedObservable({
        read: () => {
            return filters.map((filter) => ({
                field: filter.field,
                value: issueBinding(filter.value, context)
            }));
        }
    });

    const computedData = _.Bind.ComputedObservable({
        read: () => {
            const records = typeof value === 'string' ? BindingManager.issueBinding(value, context) : value;

            return records.filter((record) => {
                let match = true;

                for (let i = 0; match && i < computedFilters().length; i += 1) {
                    const filter = computedFilters()[i];
                    const recordValue = record[filter.field];

                    match = Array.isArray(filter.value) ? filter.value.includes(recordValue) : recordValue === filter.value;
                }

                return match;
            });
        }
    });

    subscriptions.push(computedFilters, computedData);

    setObservableCallback(subscriptions, computedData, (updatedValue) => onValue([...updatedValue]));

    return result;
};

const bcData = ({ value: bcName, context, onValue, filters }) => {
    const computed = computedBcParams({
        bc: bcName,
        filters: filters,
        context: context
    });
    const subscriptions = [];
    const bcSubscriptions = [];
    const result = {
        dispose: () => {
            bcSubscriptions.map(d => d.dispose());
            subscriptions.map(d => d.dispose());
        }
    };

    subscriptions.push(computed);

    setObservableCallback(subscriptions, computed, (bcParams) => {
        bcSubscriptions.map(d => d.dispose());

        let bcData;
        if (bcParams.filters.length) {
            bcData = _.Bind.ComputedObservable({
                read: () => _.BC.Filter(bcParams)
            });

            bcSubscriptions.push(bcData);
        }
        else {
            bcData = _.BC.ViewModelData(bcParams);
        }

        setObservableCallback(bcSubscriptions, bcData, value => {
            onValue([...value]);
        });
    });

    return result;
};

const computedBcParams = ({
    bc: bcName,
    filters = [],
    context
}) => {
    context = context || window;

    return _.Bind.ComputedObservable({
        owner: context,
        read: () => {
            const unwrappedFilters = filters.map(filter => ({
                field: filter.field,
                value: _.Bind.Unwrap({ value: issueBinding(filter.value, context) })
            }));

            return {
                bc: _.Bind.Unwrap({ value: issueBinding(bcName, context) }),
                filters: unwrappedFilters
            };
        }
    });
};

const bcPagination = ({
    value: bcName,
    constraints,
    context,
    onSummary,
    onHasNext,
    onHasPrevious,
    onIsLastPage,
    onStart,
    onTotal,
    onRecordCount
}) => {
    const computed = computedObservable(bcName, context);
    const subscriptions = [];
    const bcSubscriptions = [];
    const result = {
        paginateNext: () => { },
        paginatePrevious: () => { },
        dispose: () => {
            bcSubscriptions.map(d => d.dispose());
            subscriptions.map(d => d.dispose());
        }
    };

    subscriptions.push(computed);

    setObservableCallback(subscriptions, computed, (computedName) => {
        bcSubscriptions.map(d => d.dispose());

        const viewModel = _.BC.Pagination().ViewModel({
            bc: computedName
        });

        bcSubscriptions.push({
            dispose: () => {
                viewModel.Dispose();
            }
        });

        result.paginateNext = () => {
            _.BC.Pagination().Next({
                constraints: issueBinding(constraints, context),
                bc: _.BC.Instance({ bc: computedName })
            });
        };

        result.paginatePrevious = () => {
            _.BC.Pagination().Previous({
                constraints: issueBinding(constraints, context),
                bc: _.BC.Instance({ bc: computedName })
            });
        };

        setObservableCallback(bcSubscriptions, viewModel.Summary, onSummary);
        setObservableCallback(bcSubscriptions, viewModel.HasNext, onHasNext);
        setObservableCallback(bcSubscriptions, viewModel.HasPrevious, onHasPrevious);
        setObservableCallback(bcSubscriptions, viewModel.IsLastPage, onIsLastPage);
        setObservableCallback(bcSubscriptions, viewModel.Start, onStart);
        setObservableCallback(bcSubscriptions, viewModel.Total, onTotal);
        setObservableCallback(bcSubscriptions, viewModel.RecordCount, onRecordCount);
    });

    return result;
};

const readWrite = ({ value, context, onValue, onIsValid, onErrorMessage }) => {
    const subscriptions = [];
    const result = {
        updateValue: () => { },
        dispose: () => subscriptions.map(d => d.dispose())
    };

    if (onValue) {
        result.updateValue = d => onValue(d);

        const computed = computedObservable(value, context);

        subscriptions.push(computed);
        setObservableCallback(subscriptions, computed, onValue);
    }

    const rawValue = issueBinding(value, context);

    if (typeof rawValue === 'function') {
        result.updateValue = d => rawValue(d);

        setObservableCallback(subscriptions, rawValue.isValid, onIsValid);
        setObservableCallback(subscriptions, rawValue.error, onErrorMessage);
    }

    return result;
};

const setObservableCallback = (subscriptions, observable, callback) => {
    if (callback && observable && observable.subscribe) {
        callback(observable());
        subscriptions.push(observable.subscribe(callback));
    }
};

const computedObservable = (value, context) => {
    context = context || window;

    return _.Bind.ComputedObservable({
        owner: context,
        read: () => {
            return _.Bind.Unwrap({
                value: issueBinding(value, context)
            });
        }
    });
};

const issueBinding = (value, context) => {
    context = context || window;

    if (Helpers[value])
        return Helpers[value]();
    else
        return _.Bind.IssueBinding({
            context: context,
            binding: 'text',
            value: _.Content.Render({
                content: value
            })
        }).text;
};

const handleEvent = (onEvent, params) => {
    const context = params.record?.Fields ? params.record.Fields : (params.record || null);

    if (typeof onEvent === 'function') {
        onEvent(params);
    }
    else if (onEvent) {
        const binding = issueBinding(onEvent, context);

        if (typeof binding === 'function') {
            binding(params);
        }
    }
};

const buildUrl = ({ url, hash, query }, context) => {
    let result = url;

    if (query && query.length > 0) {
        result += ` + '?' + ${query.join(` + '&' + `)}`;
    }

    if (hash && hash.length > 0) {
        result += ` + '#' + ${hash.join(` + '&' + `)}`;
    }

    return encodeURI(issueBinding(result, context));
};

const BindingManager = {
    filter,
    bcData,
    bcPagination,
    readWrite,
    issueBinding,
    handleEvent,
    buildUrl,
};

export default BindingManager;